import mapXBackgroundTaskAPI from "api/backgroundTaskApi";
import {TBackgroundTaskCreate} from "api/backgroundTaskApi/types";
import {STCompany} from "api/companyApi/types";
import {STProject} from "api/projectApi/types";
import axios, {AxiosResponse, CancelTokenSource} from "axios";
import {ToastContent} from "components";
import React from "react";
import {toast} from "react-toastify";
import {setImportLinkedInProfileInfoForCurrentImport} from "store/background-tasks/backgroundTaskActions";
import {setBulkImportIdForCompany} from "store/bulk-import/bulkImportActions";
import {fireImportNotStartedToaster} from "store/bulk-import/importNotStartedToaster";
import {TAppDispatch} from "types";

const cancelTokens: {
	[key: string]: CancelTokenSource;
} = {};

/**
 * Imports LinkedIn profiles into a project.
 * Dispatches an action to store import info and triggers a notification.
 *
 * @param {string[]} linkedInLinks - Array of LinkedIn profile URLs.
 * @param {STProject} project - The project to import profiles into (optional).
 * @param type - The type of import (default: "import").
 * @returns Whether the background task was created.
 */

export const importLinkedInProfileToProject =
	(linkedInLinks: string[], project?: STProject, type = "import") =>
	async (dispatch: TAppDispatch): Promise<boolean> => {
		try {
			if (project) {
				const formBody: TBackgroundTaskCreate = {
					urls: linkedInLinks,
					project_id: project.id,
					type: "Candidates Bulk Import",
				};

				//create background task request through API
				const response = await mapXBackgroundTaskAPI.createBackgroundTaskRequest(formBody);

				if (response.status === 201) {
					// in case of bulk import, we need a toaster, no need for refresh
					if (type === "import") {
						fireToaster(project, null);
					}

					dispatch(
						setImportLinkedInProfileInfoForCurrentImport({
							...response.data,
							purpose: type,
						}),
					);

					return true;
				}

				fireImportNotStartedToaster(response);
			}
		} catch (error) {
			console.error("Error in importing LinkedIn profiles:", error);
		}

		return false;
	};

/**
 * Imports LinkedIn profiles into a company.
 * Dispatches an action to store import info and triggers a notification.
 *
 * @param {string[]} linkedInLinks - Array of LinkedIn profile URLs.
 * @param {STCompany} company - The company to import profiles into.
 */
export const importLinkedInProfileToCompany =
	(linkedInLinks: string[], company: STCompany) => async (dispatch: TAppDispatch) => {
		try {
			dispatch(setBulkImportIdForCompany(company));

			const formBody: TBackgroundTaskCreate = {
				urls: linkedInLinks,
				type: "Candidates Bulk Import",
			};

			const response = await mapXBackgroundTaskAPI.createBackgroundTaskRequest(formBody);

			if (response.status === 201) {
				// we need a toaster for company imports
				fireToaster(null, company, "candidate");

				dispatch(
					setImportLinkedInProfileInfoForCurrentImport({
						...response.data,
						purpose: "import",
					}),
				);
			} else {
				fireImportNotStartedToaster(response);
			}
		} catch (error) {
			console.error("Error in importing LinkedIn profiles:", error);
		}
	};

/**
 * Imports LinkedIn company URLs into a project.
 * Dispatches an action to store import info and triggers a notification.
 *
 * @param {string[]} companyUrls - Array of LinkedIn company URLs.
 * @param {STProject} project - The project to import companies into.
 */
export const importCompanyToProject =
	(companyUrls: string[], project: STProject) => async (dispatch: TAppDispatch) => {
		try {
			const formBody: TBackgroundTaskCreate = {
				urls: companyUrls,
				project_id: project.id,
				type: "Companies Bulk Import",
			};

			const response = await mapXBackgroundTaskAPI.createBackgroundTaskRequest(formBody);

			if (response.status === 201) {
				fireToaster(project, null, "company");

				dispatch(
					setImportLinkedInProfileInfoForCurrentImport({
						...response.data,
						linkedin_company_urls:
							response.data.linkedin_company_urls ||
							response.data.linkedin_candidate_urls ||
							companyUrls.map((url) => ({url, status: "created", reason: ""})),
						purpose: "import",
						import_type: "Companies Bulk Import",
					}),
				);
			} else {
				fireImportNotStartedToaster(response);
			}
		} catch (error) {
			console.error("Error in importing company profiles:", error);
		}
	};

/**
 * Imports LinkedIn company URLs into a market map.
 * Dispatches an action to store import info and triggers a notification.
 *
 * @param {string[]} companyUrls - Array of LinkedIn company URLs.
 * @param {number} marketMapId - The market map to import companies into.
 */
export const importCompanyToMarketMap =
	(companyUrls: string[], marketMapId: number) => async (dispatch: TAppDispatch) => {
		try {
			const formBody: TBackgroundTaskCreate = {
				urls: companyUrls,
				market_map_id: marketMapId,
				type: "Companies Bulk Import",
			};

			const response = await mapXBackgroundTaskAPI.createBackgroundTaskRequest(formBody);

			if (response.status === 201) {
				toast.success(ToastContent, {
					toastId: `market_map_company_import_started_toaster${marketMapId}`,
					autoClose: false,
					closeOnClick: false,
					data: {
						title: "The company import process has started for the market map",
						description: React.createElement(
							"div",
							null,
							React.createElement(
								"p",
								null,
								"You will receive a notification when it is done",
							),
						),
					},
					delay: 100,
				});

				dispatch(
					setImportLinkedInProfileInfoForCurrentImport({
						...response.data,
						linkedin_company_urls:
							response.data.linkedin_company_urls ||
							response.data.linkedin_candidate_urls ||
							companyUrls.map((url) => ({url, status: "created", reason: ""})),
						purpose: "import",
						import_type: "Companies Bulk Import",
						market_map_id: marketMapId,
					}),
				);
			} else {
				fireImportNotStartedToaster(response);
			}
		} catch (error) {
			console.error("Error in importing company profiles to market map:", error);
		}
	};

/**
 * Fetches the status of an individual bulk import request.
 * Uses cancel tokens to cancel previous status requests if a new one is made.
 *
 * @param {number} importId - The ID of the bulk import process.
 * @returns Import status or error.
 */
export const getIndividualBulkImportStatuses = (importId: number) => async () => {
	const requestKey = `GET_IMPORT_LINKEDIN_URL_STATUS_${importId}`;

	if (requestKey in cancelTokens) {
		cancelTokens[requestKey].cancel("Operation canceled due to new request.");
	}

	cancelTokens[requestKey] = axios.CancelToken.source();

	const config = {
		cancelToken: cancelTokens[requestKey].token,
	};

	try {
		const response: AxiosResponse = await mapXBackgroundTaskAPI.getBackgroundTasksById(
			importId,
			config,
		);

		return {
			status: response.data.status,
			data: response.data,
		};
	} catch (error) {
		return {error};
	}
};

/**
 * Displays a toast notification when a bulk import process starts.
 *
 * @param {STProject | null} project - The project where import is happening (optional).
 * @param {STCompany | null} company - The company where import is happening (optional).
 * @param {"candidate" | "company"} entityType - The type of entity being imported.
 */
const fireToaster = (
	project: Nullable<STProject>,
	company: Nullable<STCompany>,
	entityType: "candidate" | "company" = "candidate",
) => {
	const id = project?.id ?? 0;
	const name = project ? project.name : company?.name;
	const entityLabel = entityType === "company" ? "company" : "candidate";

	const title = `The ${entityLabel} import process has started  ${name ? "for" : ""} ${
		project ? " Project: " : " Company: "
	} ${name}`;
	const description = React.createElement(
		"div",
		null,
		React.createElement("p", null, "You will receive a notification when it is done"),
	);

	toast.success(ToastContent, {
		toastId: `import_started_toaster${id}`,
		autoClose: false,
		closeOnClick: false,
		data: {
			title,
			description,
		},
		delay: 100,
	});
};
